import React from "react"
import PropTypes from "prop-types"
// Material UI
import { makeStyles } from "@material-ui/core/styles"
import { Typography, Paper, CircularProgress } from "@material-ui/core"

// Icons
import CheckIcon from "@material-ui/icons/Check"
import AddIcon from "@material-ui/icons/Add"

// Components
import Button from "components/shared/Button"

const calculateTranslation = ({
  subjectQualificationRequired,
  recommended
}) => {
  let translateY = "-70%"
  if (subjectQualificationRequired && recommended) translateY = "-50%"
  else if (recommended) translateY = "-110%"
  else if (subjectQualificationRequired) translateY = "-20%"

  return translateY
}

const useStyles = makeStyles(theme => ({
  card: ({ recommended }) => ({
    backgroundColor: "#FFF",
    boxShadow: "0px 5px 15px #0000000D",
    padding: theme.spacing(5, 2.5, 5, 2.5),
    borderRadius: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minHeight: recommended ? "calc(100% + 60px)" : "100%",
    position: "relative",
    transform: recommended && "translateY(-30px)",
    border: recommended && `5px solid ${theme.palette.primary.main}`,

    [theme.breakpoints.down("md")]: {
      transform: "translateY(0)"
    }
  }),
  heading: {
    fontWeight: theme.typography.fontWeightBolder,
    marginBottom: theme.spacing(0.5)
  },
  description: {
    color: "#62687F",
    marginBottom: theme.spacing(2)
  },
  price: {
    display: "flex",
    color: "#62687F"
  },
  currencySign: {
    position: "relative",
    transform: "translateY(3px)"
  },
  figure: {
    color: "#061D3F",
    fontWeight: "600"
  },
  features: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(6)
  },
  pointWrapper: {
    marginBottom: theme.spacing(2),
    display: "flex"
  },
  point: {
    overflowWrap: "break-word",
    color: "#262626"
  },
  icon: {
    marginRight: theme.spacing(1)
  },
  buttonWrapper: ({ subjectQualificationRequired, recommended }) => ({
    position: "absolute",
    bottom: "0",
    left: "50%",
    transform: `translate(-50%,${calculateTranslation({
      subjectQualificationRequired,
      recommended
    })})`
  }),
  button: {
    width: theme.spacing(25),
    color: "#fff",
    background:
      "transparent linear-gradient(90deg, #315EFB 0%, #41B3FF 100%) 0% 0% no-repeat padding-box",
    boxShadow: "1px 3px 4px #315EFB66",
    borderRadius: theme.spacing(0.5),
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1.75, 0, 1.75, 0),
    "&.Mui-disabled": {
      color: "#fff"
    }
  },
  recommended: {
    position: "relative",
    transform: "translateY(-50%)",
    borderRadius: "20px",
    border: `1px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(1, 2, 1, 2)
  },
  subQualificationReq: {
    marginTop: theme.spacing(1),
    color: "#62687F"
  }
}))

const LearningModesCard = ({
  heading,
  description,
  features,
  canBuy,
  recommended,
  subjectQualificationRequired,
  price,
  handlePurchase,
  isEnrolled,
  hidePrice = false,
  buyLoading
}) => {
  const classes = useStyles({ recommended, subjectQualificationRequired })
  return (
    <Paper className={classes.card}>
      {recommended && (
        <Typography
          color="primary"
          align="center"
          variant="body2"
          className={classes.recommended}
        >
          Recommended For You
        </Typography>
      )}
      <Typography
        variant="h6"
        color="primary"
        className={classes.heading}
        align="center"
      >
        {heading}
      </Typography>
      <Typography
        variant="body1"
        className={classes.description}
        align="center"
      >
        {description}
      </Typography>
      {!hidePrice && (
        <div className={classes.price}>
          <Typography variant="body1" className={classes.currencySign}>
            {price.currency.symbol}
          </Typography>
          <Typography variant="h3" className={classes.figure}>
            {price.price}
          </Typography>
        </div>
      )}
      <div className={classes.features}>
        {features.map(point => (
          <div className={classes.pointWrapper} key={point.text}>
            <div className={classes.icon}>
              {point.icon === "add" ? (
                <AddIcon color="primary" />
              ) : (
                <CheckIcon color="primary" />
              )}
            </div>
            <div>
              <Typography variant="body1" className={classes.point}>
                {point.text}
              </Typography>
            </div>
          </div>
        ))}
      </div>
      <div className={classes.buttonWrapper}>
        {canBuy ? (
          <Button
            disabled={isEnrolled || buyLoading}
            className={classes.button}
            onClick={() => handlePurchase()}
          >
            <Typography variant="body1">
              {isEnrolled ? "Enrolled" : "Buy Now"}
            </Typography>
            {buyLoading && (
              <CircularProgress
                color="white"
                size="20px"
                style={{
                  marginLeft: "12px"
                }}
              />
            )}
          </Button>
        ) : (
          <Button
            disabled={isEnrolled}
            className={classes.button}
            component="a"
            href="/talk-to-a-mentor"
          >
            <Typography variant="body1">
              {isEnrolled ? "Enrolled" : "Talk to a Mentor"}
            </Typography>
          </Button>
        )}

        {subjectQualificationRequired && (
          <Typography
            variant="body2"
            className={classes.subQualificationReq}
            align="center"
          >
            * Requires a Subject Qualification
          </Typography>
        )}
      </div>
    </Paper>
  )
}

export default LearningModesCard

LearningModesCard.propTypes = {
  heading: PropTypes.string,
  canBuy: PropTypes.bool,
  description: PropTypes.string,
  features: PropTypes.array,
  recommended: PropTypes.bool,
  subjectQualificationRequired: PropTypes.bool,
  price: PropTypes.object,
  handlePurchase: PropTypes.func,
  isEnrolled: PropTypes.bool,
  hidePrice: PropTypes.bool
}
